'use client';

import { Grid, Heading, Legend, TextPassage } from '@petsmart-ui/sparky';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import type { TreatsLoginLocation } from '@libs/analytics';
import { hasAutoshipInCart, useCart } from '@libs/common-next';
import type {
    BenefitsItem,
    LinkAction,
    LoyaltyBenefitsItem,
    PRIMARY_BUTTON_STYLE,
    SECONDARY_BUTTON_STYLE,
} from '@libs/contentful';
import { NEW_TAB, TARGET_BLANK, TARGET_SELF } from '@libs/contentful';
import { NAVIGATION_URLS } from '@libs/core';

import { BenefitItems } from '../BenefitItems';
import { GenericButton } from '../GenericButton';
import type { SignInFormProps } from '../SignInForm';
import { SignInForm } from '../SignInForm';
import type { CTAWithLinkEntry } from './utils';
import { getButtonVariant } from './utils';
import './InterstitialSignIn.scss';

export type InterstitialCta = {
    fields: CTAWithLinkEntry;
};

export type InterstitialLinkAction = {
    fields: LinkAction;
};

type InterstitialSignInProps = SignInFormProps & {
    title?: string;
    subtext?: string;
    benefitItems?: BenefitsItem[] | LoyaltyBenefitsItem[];
    createAccountCta?: InterstitialCta;
    itemsTitle?: string;
    createAccountHref?: string;
    onRegisterClick?: () => void;
    displayAutoshipDescription?: boolean;
    isSoftAuthenticatedServer?: boolean;
    location?: TreatsLoginLocation;
    pathOverride?: string;
    customRedirectUrl?: string | null;
};

const componentClassName = 'interstitial-sign-in';

export function InterstitialSignIn({
    title,
    subtext,
    benefitItems,
    createAccountCta,
    itemsTitle,
    cta,
    linkAction,
    isInDrawer,
    createAccountHref,
    onRegisterClick,
    onSuccessfulSignIn,
    displayAutoshipDescription,
    isSoftAuthenticatedServer,
    location,
    pathOverride,
    customRedirectUrl,
}: Readonly<InterstitialSignInProps>) {
    const isSoftAuthenticated = false;

    const [isCheckoutPage, setIsCheckoutPage] = useState(false);

    const { cart } = useCart();
    const currentPage = usePathname();

    useEffect(() => {
        if (currentPage) {
            setIsCheckoutPage(['/checkout-login', '/cart'].includes(currentPage));
        }
    }, [currentPage]);

    const isAutoship = displayAutoshipDescription && cart ? hasAutoshipInCart(cart) : false;

    const createAccountVariant: typeof PRIMARY_BUTTON_STYLE | typeof SECONDARY_BUTTON_STYLE = createAccountCta
        ? getButtonVariant(createAccountCta)
        : 'table-header';

    const createAccountLink = isCheckoutPage
        ? `${NAVIGATION_URLS.velocity.createAccount}?redirectUrl=/checkout`
        : (createAccountHref ?? createAccountCta?.fields?.link?.fields?.webUrl ?? '/create-account');

    const signInFormProps = {
        cta,
        linkAction,
        isAutoship,
        isInDrawer,
        isSoftAuthenticatedServer,
        onSuccessfulSignIn,
        location,
        pathOverride,
        customRedirectUrl,
    };

    return (
        <div data-testid="interstitial-sign-in" className={componentClassName}>
            {isAutoship && (
                <TextPassage capLinelength={false} style={{ marginBottom: '24px' }}>
                    All autoship items require an account to checkout. Please sign in or create an account to continue.
                </TextPassage>
            )}
            {(title || subtext) && (
                <Grid.Item className={`${componentClassName}__headings`}>
                    {title && (
                        <Heading tagName="h5" className={`${componentClassName}__heading`}>
                            {title}
                        </Heading>
                    )}
                    {subtext && (
                        <Legend
                            className={`${componentClassName}__sub-heading`}
                            legendSize="sm"
                            required
                            text={subtext}
                        />
                    )}
                </Grid.Item>
            )}
            <SignInForm key="sign-in-form" {...signInFormProps} />
            {!isSoftAuthenticated && (
                <div
                    className={
                        isInDrawer
                            ? `${componentClassName}__benefits-container__drawer`
                            : `${componentClassName}__benefits-container`
                    }
                >
                    {itemsTitle && (
                        <Heading size="body-lg-bold" tagName="h2">
                            {itemsTitle}
                        </Heading>
                    )}

                    {benefitItems && <BenefitItems data-testid="benefit-items" items={benefitItems} />}
                </div>
            )}
            {/* Sparky's href variant drops onClick; capture saves the intent before navigation. */}
            {!isSoftAuthenticated && benefitItems && (
                <GenericButton
                    variant={createAccountVariant}
                    text={createAccountCta?.fields?.text || 'Create account'}
                    size="lg"
                    target={createAccountCta?.fields?.behavior === NEW_TAB ? TARGET_BLANK : TARGET_SELF}
                    href={createAccountLink}
                    onClickCapture={onRegisterClick}
                    rel="noreferrer"
                    data-testid="create-account-btn"
                    fullWidth
                />
            )}
        </div>
    );
}
